/**
 * Les filtres — le bouton « Filtres » avec le nombre de filtres actifs, et le
 * volet qu'il ouvre : des sections, « Tout réinitialiser » au pied.
 *
 *   <Filtres actifs={n} onReinitialiser={() => setF(VIDES)}>
 *     <SectionDeFiltres titre="Statut">
 *       <ChoixDePuces options={STATUTS} valeur={f.statut} onChange={(statut) => setF({ ...f, statut })} />
 *     </SectionDeFiltres>
 *     <SectionDeFiltres titre="Afficher">
 *       <Checkbox label="Non lus seulement" checked={f.nonLus} onChange={(nonLus) => setF({ ...f, nonLus })} />
 *     </SectionDeFiltres>
 *   </Filtres>
 *
 * `Filtres` pose le `BoutonFiltres` et ouvre le `PanneauDeFiltres` dans le
 * `Volet` de la librairie (lot 5 du catalogue) : porté sur <body> dans le thème
 * de l'endroit, le zoom du body, Échap et le clic dehors, le focus qui entre et
 * revient au bouton. Les deux pièces restent exportées pour un écran qui pose
 * le panneau ailleurs (un `Drawer` sur un téléphone, une colonne fixe).
 *
 * L'état vit chez l'écran (un filtre se traduit souvent en paramètre serveur) :
 * le panneau ne fait que l'afficher, et `actifs` est le nombre que l'écran
 * compte — le même calcul que sa rangée `FiltresActifs`. Dedans, ce qu'on a déjà :
 * `ChoixDePuces`, `Checkbox`, `Switch`, `Select`, `Champ`, `ChampNombre`.
 *
 * Successeur de `FiltersPopover` (conversations), `TeamFiltresPopover`,
 * `CaptionsFilterPopover`, `LeadsFiltresPopover`, et des volets « Filtre » de
 * Helios (Comptes, Telegram) : chacun refaisait le compteur et « Effacer ».
 */
import { useId, type MouseEvent, type ReactNode, type Ref } from "react";

import { Badge } from "../Badge";
import { Button, type ButtonSize } from "../Button";
import { Filter } from "../icons";
import { cn } from "../utils";
import { Volet, useVolet } from "../Volet";
import "../champs.css";
import "./saisie.css";

export interface BoutonFiltresProps {
  /** Combien de filtres dévient du défaut : un `Badge` à la marque sur le bouton. 0 = rien. */
  actifs?: number;
  /** Le texte du bouton — déjà traduit (défaut « Filtres »). */
  libelle?: string;
  /** Le panneau est ouvert (`aria-expanded`). */
  ouvert?: boolean;
  /** L'id du panneau qu'il ouvre (`aria-controls`). */
  controle?: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  /** L'icône seule, sans le mot (une barre étroite) ; le chiffre reste. */
  iconeSeule?: boolean;
  size?: ButtonSize;
  disabled?: boolean;
  /** Le bouton rendu — l'ancre d'un `Volet`. */
  ref?: Ref<HTMLButtonElement>;
  className?: string;
}

export function BoutonFiltres({
  actifs = 0,
  libelle = "Filtres",
  ouvert,
  controle,
  onClick,
  iconeSeule = false,
  size = "md",
  disabled,
  ref,
  className,
}: BoutonFiltresProps) {
  const n = Math.max(0, Math.floor(actifs));
  return (
    <Button
      ref={ref}
      variant="secondary"
      size={size}
      iconOnly={iconeSeule && n === 0}
      icon={<Filter weight="bold" className="h-[1.15em] w-[1.15em]" />}
      aria-expanded={ouvert}
      aria-haspopup="dialog"
      aria-controls={ouvert ? controle : undefined}
      aria-label={iconeSeule ? (n > 0 ? `${libelle} (${n})` : libelle) : undefined}
      title={iconeSeule ? libelle : undefined}
      disabled={disabled}
      onClick={onClick}
      className={className}
    >
      {!iconeSeule && <span>{libelle}</span>}
      {n > 0 && (
        <Badge ton="marque" className="si-compte">
          {n}
        </Badge>
      )}
    </Button>
  );
}

export interface FiltresProps {
  children: ReactNode;
  /** Combien de filtres dévient du défaut : le chiffre du bouton ; « Tout réinitialiser » s'éteint à 0. */
  actifs?: number;
  /** « Tout réinitialiser », au pied du volet. */
  onReinitialiser?: () => void;
  reinitialiserLabel?: string;
  /** Un bouton principal au pied qui referme le volet (« Voir 42 comptes ») — utile sur un téléphone. */
  validerLabel?: string;
  /** Le texte du bouton et le nom du volet — déjà traduit (défaut « Filtres »). */
  libelle?: string;
  iconeSeule?: boolean;
  size?: ButtonSize;
  /** Le volet finit au bord droit du bouton (en bout de barre, le défaut) ou part de son bord gauche. */
  alignement?: "gauche" | "droite";
  /** La largeur du volet (défaut 340 px). */
  largeur?: number;
  disabled?: boolean;
  /** Le volet ouvert, posé dans la page sous le bouton — le catalogue, une capture. */
  statique?: boolean;
  className?: string;
}

/** Le bouton « Filtres » et son volet : tout ce qu'un écran pose pour filtrer une liste. */
export function Filtres({
  children,
  actifs = 0,
  onReinitialiser,
  reinitialiserLabel,
  validerLabel,
  libelle = "Filtres",
  iconeSeule,
  size,
  alignement = "droite",
  largeur = 340,
  disabled,
  statique = false,
  className,
}: FiltresProps) {
  const v = useVolet();
  const idPanneau = useId();
  const ouvert = statique || v.ouvert;
  return (
    <div className={cn("inline-flex max-w-full flex-col", statique && alignement === "droite" ? "items-end" : "items-start", className)}>
      <BoutonFiltres
        ref={v.ancre}
        actifs={actifs}
        libelle={libelle}
        ouvert={ouvert}
        controle={idPanneau}
        iconeSeule={iconeSeule}
        size={size}
        disabled={disabled}
        onClick={statique ? undefined : v.basculer}
      />
      <Volet
        ancre={v.ancre}
        ouvert={ouvert}
        onClose={v.fermer}
        alignement={alignement}
        largeur={largeur}
        remplissage="aucun"
        ariaLabel={libelle}
        statique={statique}
        className={cn("si-volet-filtres", statique && "mt-2 max-w-full")}
      >
        <PanneauDeFiltres
          id={idPanneau}
          titre={libelle}
          actifs={actifs}
          onReinitialiser={onReinitialiser}
          reinitialiserLabel={reinitialiserLabel}
          onValider={validerLabel !== undefined ? v.fermer : undefined}
          validerLabel={validerLabel}
        >
          {children}
        </PanneauDeFiltres>
      </Volet>
    </div>
  );
}

export interface PanneauDeFiltresProps {
  children: ReactNode;
  /** Combien de filtres sont actifs : « Tout réinitialiser » s'éteint à 0. */
  actifs?: number;
  /** « Tout réinitialiser », au pied. */
  onReinitialiser?: () => void;
  reinitialiserLabel?: string;
  /** Un bouton principal au pied (« Voir les 42 comptes ») — sur un téléphone, il referme la feuille. */
  onValider?: () => void;
  validerLabel?: string;
  /** Le nom du panneau pour un lecteur d'écran (défaut « Filtres »). */
  titre?: string;
  /** L'id que le bouton cite (`aria-controls`). */
  id?: string;
  className?: string;
}

/** Le contenu du volet de filtres : les sections, puis le pied. Sans surface : celle de ce qui le porte (le `Volet`, une `Carte`, un `Drawer`). */
export function PanneauDeFiltres({
  children,
  actifs = 0,
  onReinitialiser,
  reinitialiserLabel = "Tout réinitialiser",
  onValider,
  validerLabel = "Voir les résultats",
  titre = "Filtres",
  id,
  className,
}: PanneauDeFiltresProps) {
  return (
    <div id={id} role="group" aria-label={titre} className={cn("si-panneau-filtres flex min-w-0 flex-col", className)}>
      <div className="si-panneau-filtres-corps">{children}</div>
      {(onReinitialiser || onValider) && (
        <div className="si-panneau-filtres-pied">
          {onReinitialiser ? (
            <Button variant="ghost" size="md" disabled={actifs <= 0} onClick={onReinitialiser}>
              {reinitialiserLabel}
            </Button>
          ) : (
            <span />
          )}
          {onValider && (
            <Button size="lg" onClick={onValider}>
              {validerLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export interface SectionDeFiltresProps {
  /** Le titre, en capitales (la recette de l'étiquette d'un champ). */
  titre: ReactNode;
  /** À droite du titre, en gris : « 100+ », « 2 choisies ». */
  note?: ReactNode;
  /** Sous le contenu, en gris : « Un filtre de date masque les tweets sans date. » */
  aide?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** Une section du panneau : son titre, son contenu (des puces, des cases, un champ), une aide. Sans trait entre elles. */
export function SectionDeFiltres({ titre, note, aide, children, className }: SectionDeFiltresProps) {
  const id = useId();
  return (
    <section aria-labelledby={id} className={cn("flex min-w-0 flex-col gap-2.5", className)}>
      <div className="flex items-baseline justify-between gap-3">
        <h3 id={id} className="ch-etiquette m-0">
          {titre}
        </h3>
        {note !== undefined && <span className="text-[11.5px] tabular-nums text-faint">{note}</span>}
      </div>
      <div className="flex min-w-0 flex-col gap-2">{children}</div>
      {aide !== undefined && <p className="m-0 text-[11.5px] leading-snug text-faint">{aide}</p>}
    </section>
  );
}
